import { SVGAssets } from '@business/assets/vectors';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { getPermissions } from '@ports/common/authStateSlice';
import { useSelector } from 'react-redux';
import {
  setIsFromRecipesPage,
  setMemoryFilterOptions,
  setPhotoFilterOptions,
  setRecipeFilterOptions,
  setFolderFilterOptions,
  setMomentFilterOptions,
  setPeopleFilterOptions,
  setTrashFilterOptions,
  setSharingFilterOptions,
  getAdditionalSubscriberId,
  setAdditionalSubscriberId
} from '@ports/common/sessionStateSlice';
import { useDispatch } from 'react-redux';
import { setFocusedFamilyTreePersonId } from '@ports/common/commonStateSlice';
import { routePaths } from '@utils/constants/routePaths';
import { Button } from '../Button';

interface SidebarSubItem {
  label: string;
  path: string;
}

interface SidebarItem {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  path: string;
  selectedIcon?: React.ComponentType<{ className?: string }>;
  children?: SidebarSubItem[];
  permission?: string;
}

export const Sidebar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isAnyPeopleRoute = location.pathname.startsWith('/people');
  const [openPeople, setOpenPeople] = useState<boolean>(isAnyPeopleRoute);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const permissions = useSelector(getPermissions);
  const additionalSubscriberId = useSelector(getAdditionalSubscriberId);

  useEffect(() => {
    if (isAnyPeopleRoute) {
      setOpenPeople(true);
    } else {
      setOpenPeople(false);
    }
  }, [location.pathname, isAnyPeopleRoute]);

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isMobileOpen ? 'hidden' : '';

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileOpen]);

  const defaultFilterOptions = {
    search: '',
    accessType: 'public',
    sortBy: '',
    sharedWith: '',
    people: '',
    things: '',
    locations: '',
    date: '',
    interactions: '',
    mediaType: '',
    favorites: false
  };

  const resetFilterOptionsForPath = useCallback(() => {
    // Reset ALL filters on every sidebar click to ensure clean slate
    dispatch(setPhotoFilterOptions(defaultFilterOptions));
    dispatch(setMemoryFilterOptions(defaultFilterOptions));
    dispatch(setRecipeFilterOptions(defaultFilterOptions));
    dispatch(setPeopleFilterOptions(defaultFilterOptions));
    dispatch(setFolderFilterOptions(defaultFilterOptions));
    dispatch(setMomentFilterOptions(defaultFilterOptions));
    dispatch(setTrashFilterOptions(defaultFilterOptions));
    dispatch(setSharingFilterOptions(defaultFilterOptions));
    if (additionalSubscriberId) {
      dispatch(setAdditionalSubscriberId(null));
    }
    dispatch(setFocusedFamilyTreePersonId(null));
  }, [additionalSubscriberId]);

  const sidebarItems: SidebarItem[] = [
    {
      icon: SVGAssets.FavoritesIcon,
      label: t('favorites'),
      path: '/favorites',
      selectedIcon: SVGAssets.FavWhite,
      permission: 'favorites:view'
    },
    {
      icon: SVGAssets.PhotosIcon,
      label: t('photos'),
      path: '/photos',
      selectedIcon: SVGAssets.PhotoWhite,
      permission: 'photos:view'
    },
    {
      icon: SVGAssets.PeopleIcon,
      label: t('people'),
      path: '/people',
      selectedIcon: SVGAssets.PeopleWhite,
      permission: 'people:view',
      children: [
        {
          label: t('family'),
          path: '/people/family'
        },
        {
          label: t('friends'),
          path: '/people/friends'
        }
      ]
    },
    {
      icon: SVGAssets.MemoriesIcon,
      label: t('memories'),
      path: '/memories',
      permission: 'memories:view',
      selectedIcon: SVGAssets.MemoryWhite
    },
    {
      icon: SVGAssets.FamilyTreeIcon,
      label: t('familyTree'),
      selectedIcon: SVGAssets.TreeWhite,
      permission: 'familyTree:view',
      path: '/family-tree'
    },
    {
      icon: SVGAssets.RecipesIcon,
      label: t('recipes'),
      path: '/recipes',
      selectedIcon: SVGAssets.RecipieWhite,
      permission: 'recipes:view'
    },
    {
      icon: SVGAssets.Shares,
      label: t('sharing'),
      path: '/sharing',
      selectedIcon: SVGAssets.ShareWhite,
      permission: 'sharing:view'
    },
    {
      icon: SVGAssets.StorageIcon,
      label: t('storage'),
      path: '/storage',
      selectedIcon: SVGAssets.StorageWhite,
      permission: 'storage:view'
    },
    {
      icon: SVGAssets.TrashIcon,
      label: t('trash'),
      path: '/trash',
      selectedIcon: SVGAssets.TrashWhite,
      permission: 'trash:view'
    },
    {
      icon: SVGAssets.TutorialIcon,
      label: t('tutorials'),
      path: routePaths.Tutorial,
      selectedIcon: SVGAssets.TutorialWhite
    }
  ];

  // const isActive = (path: string) => location.pathname.startsWith(path);

  const isParentActive = (item: SidebarItem) => {
    if (!item.path) return false;
    // For items with children, only mark active if it's the exact path or a child path
    if (item.children) {
      return (
        location.pathname === item.path ||
        item.children.some(child => location.pathname.startsWith(child.path))
      );
    }
    if (
      item.path === routePaths.Sharing &&
      location.pathname.startsWith(routePaths.SharedFamilyTree)
    ) {
      return true;
    }
    return location.pathname.startsWith(item.path);
  };

  const handleNavigate = useCallback(
    (path: string) => {
      resetFilterOptionsForPath();
      navigate(path);
      setIsMobileOpen(false);
    },
    [navigate, resetFilterOptionsForPath]
  );

  const handleItemClick = useCallback(
    (item: SidebarItem) => {
      if (item.path === '#') return;

      if (item.children) {
        dispatch(setIsFromRecipesPage(false));
        handleNavigate(item.path);
        setOpenPeople(!openPeople);
        return;
      }

      if (item.path) {
        handleNavigate(item.path);
      }
    },
    [dispatch, handleNavigate, openPeople]
  );

  const sidebarContent = (
    <nav className="p-4 space-y-1 overflow-y-auto">
      {sidebarItems.map((item, index) => {
        const active = isParentActive(item);
        const Icon =
          active && item.selectedIcon ? item.selectedIcon : item.icon;

        return (
          <div key={index}>
            <Button
              btnStyle="link"
              disabled={
                item.permission
                  ? !permissions?.includes(item.permission)
                  : false
              }
              onClick={() => handleItemClick(item)}
              className={`
            w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors
            ${
              active
                ? 'bg-[#49712B] text-white font-medium'
                : (
                      item.permission
                        ? permissions?.includes(item.permission)
                        : true
                    )
                  ? 'text-[#505050] hover:bg-[#ebe5e5]'
                  : 'text-[#9CA3AF]'
            }
          `}>
              <Icon className="w-5 h-5" />
              <span className="text-sm font-semibold">{item.label}</span>
            </Button>

            {item?.children &&
              openPeople &&
              (!item.permission || permissions?.includes(item.permission)) && (
                <div className="ml-10 mt-2 space-y-3">
                  {item?.children.map((sub, idx) => {
                    const subActive = location?.pathname === sub?.path;

                    return (
                      <button
                        onClick={() => handleNavigate(sub?.path)}
                        key={idx}
                        className={`w-full text-left flex items-center gap-3 px-4 py-2 text-xs ml-2 rounded-lg text-[#505050] font-semibold ${subActive ? 'bg-[#ebe5e5]' : 'hover:bg-[#ebe5e5]'}`}>
                        <span>{sub?.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
          </div>
        );
      })}
    </nav>
  );

  return (
    <>
      {!isMobileOpen && (
        <button
          type="button"
          aria-label="Open navigation"
          onClick={() => setIsMobileOpen(true)}
          className="fixed left-2 top-5 z-[60] inline-flex h-11 w-11 items-center justify-center text-[#49712B] md:hidden">
          <span className="flex flex-col gap-1.5">
            <span className="block h-0.5 w-5 rounded-full bg-current" />
            <span className="block h-0.5 w-5 rounded-full bg-current" />
            <span className="block h-0.5 w-5 rounded-full bg-current" />
          </span>
        </button>
      )}

      {isMobileOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={() => setIsMobileOpen(false)}
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-[60] flex w-64 -translate-x-full flex-col border-r border-gray-200 bg-white transition-transform duration-300 ease-out md:static md:z-auto md:translate-x-0 md:flex-shrink-0 ${
          isMobileOpen ? 'translate-x-0 shadow-xl' : ''
        }`}>
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-4 md:hidden">
          <span className="text-sm font-semibold uppercase tracking-[0.2em] text-[#49712B]">
            {t('menu')}
          </span>
          <button
            type="button"
            aria-label="Close navigation"
            onClick={() => setIsMobileOpen(false)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-[#505050] hover:bg-[#ebe5e5]">
            <span className="relative block h-4 w-4">
              <span className="absolute left-1/2 top-1/2 h-0.5 w-4 -translate-x-1/2 -translate-y-1/2 rotate-45 rounded-full bg-current" />
              <span className="absolute left-1/2 top-1/2 h-0.5 w-4 -translate-x-1/2 -translate-y-1/2 -rotate-45 rounded-full bg-current" />
            </span>
          </button>
        </div>
        {sidebarContent}
      </aside>
    </>
  );
};
